import { createTheme } from "@mui/material/styles";

const theme = createTheme({
    palette: {
        mode: "light",

        // main deep green (header, important actions)
        primary: {
            main: "#1F5A36",     // deep forest green
            light: "#4D825E",
            dark: "#123721",

        },

        // warm orange accent (buttons / highlights)
        secondary: {
            main: "#FF9016",     // CTA orange
            light: "#FFBE55",
            dark: "#Fc8712",
        },

        background: {
            // page background – warm cream like in the design
            default: "#fbeed2ff",
            // cards / sections
            paper: "#FFFFFF",
        },

        text: {
            primary: "#222422",   // dark, slightly warm
            secondary: "#5F6158", // grey-green for subtitles / body
        },

        success: {
            main: "#2E7D32",      // for good trends, status badges
        },
        warning: {
            main: "#F6A623",
        },
        error: {
            main: "#c8352dff",
        },

        divider: "#FFFBEF",     // soft beige divider like card borders
        dialogBackground: "#C3E3C0",
    },

    // rounded cards & sections like in the screenshot
    shape: {
        borderRadius: 18,
    },

    // slightly larger spacing for airy layout
    spacing: 8, // spacing(1) = 8px

    typography: {
        // you can load this in index.html via Google Fonts
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',

        h1: { fontWeight: 700, fontSize: "2.6rem" }, // hero heading
        h2: { fontWeight: 700, fontSize: "2.1rem" },
        h3: { fontWeight: 600, fontSize: "1.8rem" },
        h4: { fontWeight: 600, fontSize: "1.5rem" },
        h5: { fontWeight: 600, fontSize: "1.25rem" },
        h6: { fontWeight: 600, fontSize: "1.05rem" },

        body1: { fontSize: "1rem", lineHeight: 1.7 },
        body2: { fontSize: "0.9rem", lineHeight: 1.6 },

        button: {
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: 0.3,
        },
    },

    components: {
        MuiButton: {
            defaultProps: {
                variant: "contained",
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    paddingInline: 22,
                    paddingBlock: 8,
                },
                sizeSmall: {
                    paddingInline: 18,
                    paddingBlock: 6,
                },
                contained: {
                    backgroundColor: "#Fc8712", // secondary.dark
                    color: "#FFFFFF",
                    fontWeight: 600,
                    transition: "all 0.3s ease",
                    "&:hover": {
                        backgroundColor: "#FF9016", // secondary.main
                        transform: "scale(1.03)",
                    },
                },
            },
        },

        // Cards / white blocks over the cream background
        MuiPaper: {
            defaultProps: {
                elevation: 1,
            },
            styleOverrides: {
                root: {
                    borderRadius: 18,
                    padding: 24,
                },
            },
        },

        MuiCard: {
            defaultProps: {
                elevation: 2,
            },
            styleOverrides: {
                root: {
                    borderRadius: 18,
                },
            },
        },

        // Top navbar like the dark green header bar
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundColor: "#123721",
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                },
            },
        },

        // Navigation items (top menu / side menu)
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    paddingInline: 16,
                    marginBlock: 4,
                    "&.Mui-selected": {
                        backgroundColor: "rgba(246,166,35,0.12)", // soft orange
                        color: "#1F5A36",
                    },
                },
            },
        },

        // Text fields for login / forms
        MuiTextField: {
            defaultProps: {
                variant: "outlined",
                size: "small",
                fullWidth: true,
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    backgroundColor: "#FFFFFF",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#C8D2C4",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1F5A36",
                    },
                },
                input: {
                    paddingBlock: 12,
                    paddingInline: 12,
                },
            },
        },

        MuiSelect:{

        },

        // Tabs (like the top nav tabs / section filters)
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: 4,
                    borderRadius: 999,
                    backgroundColor: "#F6A623",
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontWeight: 500,
                    minWidth: 0,
                },
            },
        },

        // Chips / small pills for filters/tags
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    fontWeight: 500,
                },
            },
        },

        MuiDrawer: {
            styleOverrides: {
                paper: { borderRadius: 0, },
            },
        },
    },
});

export default theme;